import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

interface DefaultHeroProps {
  headline: string;
  subtext?: string;
  primaryCta?: { text: string; href: string };
  secondaryCta?: { text: string; href: string };
  backgroundImage?: string;
  align?: "center" | "left";
}

const DefaultHero = ({
  headline,
  subtext,
  primaryCta,
  secondaryCta,
  backgroundImage,
  align = "center",
}: DefaultHeroProps) => {
  const hasImage = !!backgroundImage;

  return (
    <section
      className={cn(
        "relative w-full overflow-hidden pt-36 pb-20 md:pt-44 md:pb-28",
        !hasImage && "bg-secondary on-dark"
      )}
    >
      {/* Background image */}
      {hasImage && (
        <>
          <div className="absolute inset-0" aria-hidden="true">
            <img
              src={backgroundImage}
              alt=""
              className="w-full h-full object-cover"
              loading="eager"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-rich-black/80 via-rich-black/50 to-rich-black/30" aria-hidden="true" />
        </>
      )}

      {/* Content */}
      <div
        className={cn(
          "relative z-10 mx-auto max-w-wide px-5 md:px-12",
          align === "center" ? "text-center" : "text-left max-w-wide"
        )}
      >
        <div className={cn(align === "center" ? "max-w-3xl mx-auto" : "max-w-2xl")}>
          <h1
            className={cn(
              "font-headline text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-tight",
              hasImage ? "text-cream" : "text-foreground"
            )}
          >
            {headline}
          </h1>

          {subtext && (
            <p
              className={cn(
                "mt-4 md:mt-6 text-sm md:text-base lg:text-lg leading-relaxed",
                hasImage ? "text-cream/80" : "text-muted-foreground"
              )}
            >
              {subtext}
            </p>
          )}

          {/* CTAs */}
          {(primaryCta || secondaryCta) && (
            <div
              className={cn(
                "mt-8 md:mt-10 flex flex-wrap gap-4",
                align === "center" ? "justify-center" : "justify-start"
              )}
            >
              {primaryCta && (
                <Link
                  to={primaryCta.href}
                  className="bg-accent text-accent-foreground font-body text-sm uppercase tracking-widest px-8 py-3.5 hover:bg-accent/90 transition-colors inline-block"
                >
                  {primaryCta.text}
                </Link>
              )}
              {secondaryCta && (
                <Link
                  to={secondaryCta.href}
                  className={cn(
                    "border font-body text-sm uppercase tracking-widest px-8 py-3.5 transition-colors inline-block",
                    hasImage
                      ? "border-cream/40 text-cream hover:bg-cream/10"
                      : "border-foreground/30 text-foreground hover:bg-foreground/5"
                  )}
                >
                  {secondaryCta.text}
                </Link>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default DefaultHero;
